import { useCallback, useState } from 'react';
import { Badge } from '@/components/Badge/Badge';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { isPresenter, usePrincipal } from '@/features/auth/identity';
import { api } from '@/lib/api/endpoints';
import type { SessionState } from '@/lib/api/types';
import { formatDateTime, padIndex } from '@/lib/format';
import { usePolling } from '@/lib/hooks/usePolling';
import { useSubmit } from '@/lib/hooks/useSubmit';
import styles from './pages.module.scss';

/**
 * Live session control (US-4.1/US-4.2).
 *
 * The presenter publishes a position; attendees in follow mode pick it up on
 * their next poll. The payload is two integers — position identifiers only,
 * never content, notes, or source paths (NFR-AP1). Presenter permissions are
 * separable from administration (BR-AP2), so this card is gated on the
 * presenter role rather than on the admin claim — cosmetically here, and
 * authoritatively in the Worker.
 */
export function PresenterControls({ cohortId }: { cohortId: string }) {
  const principal = usePrincipal();
  const [pending, setPending] = useState<SessionState | null>(null);

  const fetcher = useCallback(
    (signal: AbortSignal) => api.session.get(cohortId, signal),
    [cohortId],
  );
  const { data, error } = usePolling<SessionState>(fetcher, { intervalMs: 5000 });

  const { submit, busy, message } = useSubmit(
    (position: { module: number; topic: number }) => api.session.advance(cohortId, position),
    (result) => setPending(result),
  );

  if (!isPresenter(principal)) return null;

  const current = pending ?? data;
  const position = { module: current?.module ?? 0, topic: current?.topic ?? 0 };

  const move = (delta: { module?: number; topic?: number }) =>
    void submit({
      module: Math.max(0, position.module + (delta.module ?? 0)),
      topic: Math.max(0, position.topic + (delta.topic ?? 0)),
    });

  return (
    <Card
      title="Live session (US-4.1)"
      actions={<Badge tone={data ? 'live' : 'neutral'}>{data ? 'session state live' : 'no session'}</Badge>}
      footer={
        current?.updatedAt
          ? `Position last published ${formatDateTime(current.updatedAt)}.`
          : 'No position published for this cohort yet.'
      }
    >
      <p className={styles.note}>
        Attendees following this session are on module {padIndex(position.module)}, topic{' '}
        {position.topic + 1}. Changing it here moves every follower without a page refresh.
      </p>

      <div className={styles.inlineActions}>
        <Button size="sm" onClick={() => move({ module: -1 })} disabled={busy || position.module === 0}>
          ← Module
        </Button>
        <Button size="sm" onClick={() => move({ topic: -1 })} disabled={busy || position.topic === 0}>
          ← Topic
        </Button>
        <Button size="sm" variant="primary" onClick={() => move({ topic: 1 })} disabled={busy}>
          Topic →
        </Button>
        <Button size="sm" onClick={() => move({ module: 1, topic: -position.topic })} disabled={busy}>
          Module →
        </Button>
      </div>

      {(message ?? error) && <p className={styles.note}>{message ?? error}</p>}
    </Card>
  );
}
